import mixpanel from 'mixpanel-browser'
import Sentry from '@/services/sentry'

const AHABIGQ_INSTANCE_NAME = 'ahabigq'

let instance = null

const MAX_PENDING_IDENTITY_CALLS = 20
const pendingIdentityCalls = []

const generateInsertId = () => `${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`

const withSharedEventIds = (props) => ({
  $insert_id: generateInsertId(),
  time: Date.now() / 1000,
  ...props,
})

const resolveInstance = (initResult) => initResult || mixpanel[AHABIGQ_INSTANCE_NAME] || null

const PERSONAL_PROPERTY_KEYS = ['$email', '$first_name', '$last_name', '$name', '$phone']

const syncSuperProperties = (target) => {
  const props = { ...mixpanel.persistence?.properties?.() }
  PERSONAL_PROPERTY_KEYS.forEach((key) => delete props[key])
  // register() merges, so a user id Mixpanel dropped while persistence was disabled would otherwise stay on later anonymous events
  Object.keys(target.persistence?.properties?.() || {}).forEach((key) => {
    if (!(key in props)) target.unregister(key)
  })
  if (Object.keys(props).length) target.register(props)
}

const replayPendingCalls = (pendingCalls, target) => {
  pendingCalls.splice(0).forEach((callback) => {
    try {
      callback(target)
    } catch {
      // AhaBigQ must never affect Mixpanel or the UI
    }
  })
}

const init = (url, projectToken, options = {}) => {
  if (!url || !projectToken) return
  if (instance) {
    instance.set_config({ disable_persistence: options.disable_persistence === true })
    syncSuperProperties(instance)
    return
  }
  try {
    // mixpanel-js keys its cookie and batch queue by token, so a same-token instance would flush Mixpanel's queued events to AhaBigQ
    instance = resolveInstance(mixpanel.init(`${AHABIGQ_INSTANCE_NAME}_${projectToken}`, {
      ...options,
      api_host: url,
      record_sessions_percent: 0,
      persistence_name: AHABIGQ_INSTANCE_NAME,
      batch_requests: true,
      persistence: 'localStorage',
    }, AHABIGQ_INSTANCE_NAME))
    if (instance) {
      syncSuperProperties(instance)
      replayPendingCalls(pendingIdentityCalls, instance)
    }
  } catch (error) {
    instance = null
    Sentry.captureException(error)
  }
}

const mirror = (callback) => {
  if (!instance) return
  try {
    callback(instance)
  } catch {
    // AhaBigQ must never affect Mixpanel or the UI
  }
}

const mirrorIdentity = (callback) => {
  if (!instance) {
    if (pendingIdentityCalls.length >= MAX_PENDING_IDENTITY_CALLS) pendingIdentityCalls.shift()
    pendingIdentityCalls.push(callback)
    return
  }
  mirror(callback)
}

const reset = () => {
  pendingIdentityCalls.length = 0
  mirror((bigQ) => {
    bigQ.reset()
    syncSuperProperties(bigQ)
  })
}

export default {
  init,
  mirror,
  mirrorIdentity,
  withSharedEventIds,
  reset,
}
